<script setup>
import SiteLayout from '@/Layouts/Layout.vue';
import Card from '@/Components/Card/Card.vue';
import { inject, provide, reactive, ref, watch } from 'vue';
import PrimaryButton from '@/Components/Button/PrimaryButton.vue';
import RiskLabel from '@/Pages/DomainScore/Partials/RiskLabel.vue';
import RiskDescription from '@/Pages/DomainScore/Partials/RiskDescription.vue';
import CircularScore from '@/Components/Chart/CircularScore.vue';
import RiskPill from '@/Pages/Common/RiskPill.vue';
import RatingCard from '@/Pages/DomainScore/Partials/RatingCard.vue';
import DetailedReport from '@/Pages/DomainScore/Partials/DetailedReport.vue';
import FlaredScoreCard from '@/Pages/DomainScore/Partials/FlaredScoreCard.vue';
import { useForm } from '@inertiajs/vue3';
import Alert from '@/Components/Alert.vue';
import { isValidDomain } from '@/Common/IsValidDomain.js';
import Pill from '@/Components/Pill.vue';
import DomainInput from '@/Pages/Common/DomainInput.vue';
import ACTION_TYPES from '@/Common/Enum/ActionType.js';
import PartnerLogo from '@/Components/Logo/PartnerLogo.vue';
import EmailReportModal from './Modals/EmailReportModal.vue';
import SecondaryButton from '@/Components/Button/SecondaryButton.vue';
import OutlinedButton from '@/Components/Button/OutlinedButton.vue';
import IconExpandMore from '@/Components/Icons/IconExpandMore.vue';
import { usePage } from '@inertiajs/vue3';
import { useI18n } from 'vue-i18n';
import {
  TOOL_DOMAIN_SCORE,
  captureDetailsExpanded,
  captureReportRequested,
  captureToolUsed,
  dmarcProviderHostsFromRua
} from '@/Common/Posthog.js';

const { t } = useI18n();
const page = usePage();
const branding = page.props.branding;
const props = defineProps({
  score: Object,
  domain: String,
  errorTitle: String,
  partnerLogo: {
    type: [Object, null]
  },
  visitorCountry: {
    type: String,
    default: 'ZA'
  }
});

const layout = inject('layout');

const state = reactive({
  showDetailedReport: layout === 'pdf',
  domainName: props.domain,
  error: null
});

const form = useForm({});

provide('domain', props.domain);
provide('score', props.score);

const scoreDomain = () => {
  state.error = null;

  if (!isValidDomain(state.domainName)) {
    state.error = t('domain_score.index.valid_domain_name');
    return;
  }
  form.get(route('score.show', state.domainName));
};
const showModal = ref(false);

const toggleEmailReportModal = () => {
  showModal.value = !showModal.value;
};

if (layout !== 'pdf') {
  watch(
    () => (props.score?.score_pct != null ? props.domain : null),
    (testedDomain) => {
      if (!testedDomain) {
        return;
      }
      captureToolUsed({
        tool: TOOL_DOMAIN_SCORE,
        properties: {
          tested_domain: testedDomain,
          overall_score: props.score.score_pct,
          impersonation_score: props.score.impersonation?.score,
          privacy_score: props.score.privacy?.score,
          branding_score: props.score.marketing?.score,
          dmarc_policy: props.score.records?.dmarc?.policy ?? '',
          dmarc_provider_hosts: dmarcProviderHostsFromRua(
            props.score.records?.dmarc?.aggregate_reporting_addresses
          )
        }
      });
    },
    { immediate: true }
  );

  watch(
    () => state.showDetailedReport,
    (open) => {
      if (!open) {
        return;
      }
      captureDetailsExpanded({
        tool: TOOL_DOMAIN_SCORE,
        testedDomain: props.domain
      });
    }
  );

  watch(showModal, (open) => {
    if (!open) {
      return;
    }
    captureReportRequested({
      step: 'opened',
      tool: TOOL_DOMAIN_SCORE,
      testedDomain: props.domain
    });
  });
}
</script>

<template>
  <SiteLayout
    :title="$t('domain_score.show.domain_score_for', { domain: domain })"
    :description="
      $t('domain_score.show.domain_score_for_description', { domain: domain })
    "
  >
    <template #headerRight>
      <PartnerLogo v-if="partnerLogo" :partner-logo="partnerLogo" />
      <PartnerLogo
        v-else-if="branding?.co_branded_logo"
        :partner-logo="branding?.co_branded_logo"
      />
    </template>
    <Card v-if="layout === 'web'">
      <form @submit.prevent="scoreDomain">
        <div
          class="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center"
        >
          <DomainInput
            v-model="state.domainName"
            required
            redirect-to="score.show"
            :error-message="state.error"
            class="flex w-full flex-1"
          />
          <div>
            <PrimaryButton
              size="large"
              class="mt-2"
              :is-loading="form.processing"
            >
              {{ $t('domain_score.index.get_your_domain_score') }}
            </PrimaryButton>
          </div>
        </div>
        <Alert
          v-if="score.hasOwnProperty('detail')"
          class="mt-10"
          :title="errorTitle"
          variant="danger"
        >
          {{ score.detail }}
        </Alert>
      </form>
    </Card>
    <FlaredScoreCard
      v-if="score.hasOwnProperty('score_pct')"
      :score="score.score_pct"
    >
      <div class="relative z-10">
        <div class="mb-2 text-xl">
          <i18n-t keypath="domain_score.show.domain_score_for" tag="span">
            <template #domain>
              <strong>{{ domain }}</strong>
            </template>
          </i18n-t>
        </div>
        <div class="flex gap-4">
          <div class="flex w-full shrink-0 flex-col lg:w-1/2">
            <div class="flex">
              <div class="w-full lg:w-2/3">
                <RiskLabel
                  class="text-2xl md:text-3xl"
                  :score="score.score_pct"
                  :use-rating-to-calculate="false"
                />
                <RiskDescription :score="score.score_pct" />
                <div class="my-5 flex gap-2">
                  <Pill
                    :variant="score.records?.ns?.score ? 'success' : 'default'"
                  >
                    {{ $t('domain_score.show.ns') }}
                  </Pill>
                  <Pill
                    :variant="score.records?.mx?.score ? 'success' : 'default'"
                  >
                    {{ $t('domain_score.show.mx') }}
                  </Pill>
                  <Pill
                    :variant="score.records?.a?.score ? 'success' : 'default'"
                  >
                    {{ $t('domain_score.show.a') }}
                  </Pill>
                </div>
              </div>

              <div class="flex flex-col items-center">
                <div class="mb-1 pt-0 font-bold md:block">
                  {{ $t('domain_score.show.overall_score') }}
                </div>
                <CircularScore class="max-w-[180px]" :score="score.score_pct" />
              </div>
            </div>

            <div
              v-if="layout === 'web'"
              class="col-span-3 flex items-center justify-between"
            >
              <div class="flex gap-3">
                <SecondaryButton
                  @click="state.showDetailedReport = !state.showDetailedReport"
                >
                  <div class="flex items-center gap-1">
                    <IconExpandMore
                      class="shrink-0 transform cursor-pointer text-nowrap transition-transform duration-300 ease-in-out"
                      :class="{ 'rotate-180': state.showDetailedReport }"
                    />
                    {{ $t('domain_score.show.view_detailed_report') }}
                  </div>
                </SecondaryButton>
                <OutlinedButton
                  v-if="branding?.email_report"
                  @click="toggleEmailReportModal"
                  datadog-action-name="Email Report - top"
                >
                  {{ $t('domain_score.show.email_report') }}
                </OutlinedButton>
              </div>
            </div>
          </div>

          <RatingCard
            :title="$t('domain_score.show.impersonation')"
            lookup="impersonation"
            :score="score"
            :pills="{ dmarc: 'DMARC', spf: 'SPF' }"
          >
            <RiskPill
              tag="DKIM"
              :score="score.records['dkim'].score"
              :variant="
                (score.records['dkim'].num_dkim_records === 0 &&
                  ACTION_TYPES.INFO_DEFAULT) ||
                null
              "
            />
          </RatingCard>
          <RatingCard
            :title="$t('domain_score.show.privacy')"
            lookup="privacy"
            :score="score"
            :pills="{ tlsrpt: 'TLS-RPT', mtasts: 'MTA-STS' }"
          />
          <RatingCard
            :title="$t('domain_score.show.branding')"
            lookup="marketing"
            :score="score"
            :pills="{ bimi: 'BIMI' }"
          >
            <RiskPill
              :score="score.records.bimi.has_certificate ? 100 : 0"
              tag="Certificate"
            />
            <RiskPill
              :score="score.records.bimi.has_valid_logo ? 100 : 0"
              tag="Image"
            />
          </RatingCard>
        </div>
      </div>

      <DetailedReport
        v-if="state.showDetailedReport"
        @email-report="toggleEmailReportModal"
      />
      <EmailReportModal
        v-if="showModal"
        :domain="domain"
        @close="toggleEmailReportModal"
        :default-country="visitorCountry"
      />
    </FlaredScoreCard>
  </SiteLayout>
</template>
